// hook/useTableEngine.ts
import * as React from 'react';
import { Column, TableProps, TableRow, RowId } from './types';

type SortState = {
    columnId?: string;
    direction: 'asc' | 'desc';
};

type EditingCell = {
    rowId: RowId;
    columnId: string;
};

type EngineState = {
    globalFilter: string;
    columnFilters: Map<string, string>;
    sort: SortState;
    selected: Set<RowId>;
    editingCell?: EditingCell | null;
};

/** Lấy "giá trị thô" của một ô theo định nghĩa cột */
function getCellRaw<T>(row: TableRow<T>, col: Column<T>) {
    if (col.accessor) return col.accessor(row.data) as any;
    return (row.data as any)[col.id];
}

/** Chuyển giá trị ô thành text đơn giản để filter/sort */
function toText(val: any): string {
    const v = (val as any)?.props?.children ?? val;
    if (v == null) return '';
    return String(v).toLowerCase();
}

export function useTableEngine<T>(props: TableProps<T>) {
    const { columns, rows: inputRows = [] } = props;

    const [state, setState] = React.useState<EngineState>({
        globalFilter: '',
        columnFilters: new Map(),
        sort: { columnId: undefined, direction: 'asc' },
        selected: new Set<RowId>(),
        editingCell: null,
    });

    // ========= Filtering (global + per-column) =========
    const filteredRows = React.useMemo(() => {
        const g = state.globalFilter.trim().toLowerCase();
        const hasGlobal = g.length > 0;
        const hasColFilters = state.columnFilters.size > 0;

        if (!hasGlobal && !hasColFilters) return inputRows;

        return inputRows.filter((r) => {
            // Global filter: khớp với bất kỳ cột nào
            const matchGlobal =
                !hasGlobal || columns.some((c) => toText(getCellRaw(r, c)).includes(g));
            if (!matchGlobal) return false;

            // Per-column filters
            for (const [cid, val] of state.columnFilters.entries()) {
                const col = columns.find((c) => c.id === cid);
                if (!col) continue;
                const needle = val.trim().toLowerCase();
                if (needle && !toText(getCellRaw(r, col)).includes(needle)) return false;
            }
            return true;
        });
    }, [inputRows, columns, state.globalFilter, state.columnFilters]);

    // ========= Sorting: CHỈ sort trong block (city+role), giữ nguyên header/ranh giới =========
    const sortedRows = React.useMemo(() => {
        const { columnId, direction } = state.sort;
        if (!columnId) return filteredRows;

        const col = columns.find((c) => c.id === columnId);
        if (!col || col.sortable === false) return filteredRows;

        const sign = direction === 'asc' ? 1 : -1;

        type Kind = 'group-city' | 'group-role' | 'detail' | undefined;
        const getKind = (r: TableRow<T>): Kind => (r.data as any)?.kind;
        const getGroupKey = (r: TableRow<T>) => {
            const d: any = r.data;
            return d?.kind === 'detail' ? `${d.city ?? ''}|${d.role ?? ''}` : null;
        };

        const copy = filteredRows.slice();

        copy.sort((a, b) => {
            const ka = getKind(a);
            const kb = getKind(b);

            // Header nhóm (city/role): không đổi vị trí với ai
            if (ka !== 'detail' || kb !== 'detail') return 0;

            // Chỉ sort nếu 2 dòng cùng một nhóm (cùng city+role)
            const ga = getGroupKey(a);
            const gb = getGroupKey(b);
            if (!ga || !gb) return 0;
            if (ga !== gb) return 0;

            // So sánh theo cột được chọn
            const va = toText(getCellRaw(a, col));
            const vb = toText(getCellRaw(b, col));
            if (va < vb) return -1 * sign;
            if (va > vb) return 1 * sign;
            return 0; // ổn định trong block
        });

        return copy;
    }, [filteredRows, columns, state.sort]);

    // ========= Selection =========
    const toggleSelectRow = (rowId: RowId) => {
        setState((s) => {
            const next = new Set<RowId>(s.selected);
            next.has(rowId) ? next.delete(rowId) : next.add(rowId);
            return { ...s, selected: next };
        });
    };

    // Không phân trang: "Select all" áp trên TẬP HÀNG ĐANG HIỂN THỊ (sau filter/sort)
    const toggleSelectAllPage = () => {
        setState((s) => {
            const allIds: RowId[] = sortedRows.map((r) => r.id);
            const allSelected =
                allIds.length > 0 && allIds.every((id) => s.selected.has(id));
            const next = new Set<RowId>(s.selected);

            if (allSelected) {
                // Bỏ chọn tất cả hàng hiện tại
                allIds.forEach((id) => next.delete(id));
            } else {
                // Chọn tất cả hàng hiện tại
                allIds.forEach((id) => next.add(id));
            }
            return { ...s, selected: next };
        });
    };

    // ========= Sorting toggle =========
    const toggleSort = (columnId: string) => {
        setState((s) => {
            if (s.sort.columnId === columnId) {
                const nextDir = s.sort.direction === 'asc' ? 'desc' : 'asc';
                return { ...s, sort: { columnId, direction: nextDir } };
            }
            return { ...s, sort: { columnId, direction: 'asc' } };
        });
    };

    // ========= Filters setters =========
    const setGlobalFilter = (value: string) =>
        setState((s) => ({ ...s, globalFilter: value ?? '' }));

    const setColumnFilter = (columnId: string, value: string) =>
        setState((s) => {
            const next = new Map(s.columnFilters);
            if (value?.length) next.set(columnId, value);
            else next.delete(columnId);
            return { ...s, columnFilters: next };
        });

    // ========= Inline edit =========
    const startEdit = (rowId: RowId, columnId: string) =>
        setState((s) => ({ ...s, editingCell: { rowId, columnId } }));

    const stopEdit = () => setState((s) => ({ ...s, editingCell: null }));

    // ========= Kết quả trả về =========
    return {
        state,
        rows: sortedRows, // KHÔNG còn pageRows
        setGlobalFilter,
        setColumnFilter,
        toggleSort,
        toggleSelectAllPage,
        toggleSelectRow,
        startEdit,
        stopEdit,
    };
}
